import {
  BillingDetails,
  type CompanyLicense,
  INVOICE_STATUS_TITLES,
  type Invoice,
  PAYMENT_METHOD_TITLES,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const INVOICES_KEY = ['admin', 'license', 'invoices'];
const BILLING_KEY = ['admin', 'billing'];

function money(n: number, currency: string): string {
  return `${n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function statusOf(i: Invoice): { title: string; tone: Tone } {
  if (i.status === 'paid') return { title: INVOICE_STATUS_TITLES.paid, tone: 'success' };
  if (i.status === 'cancelled') return { title: INVOICE_STATUS_TITLES.cancelled, tone: 'neutral' };
  if (i.overdue) return { title: INVOICE_STATUS_TITLES.overdue, tone: 'danger' };
  return { title: INVOICE_STATUS_TITLES.issued, tone: 'warning' };
}

const esc = (s: unknown) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Печатная форма счёта: открывается в новом окне и сразу уходит на печать (А4) */
function printInvoice(i: Invoice) {
  const s = i.seller as Record<string, unknown>;
  const b = i.buyer as Record<string, unknown>;
  const party = (p: Record<string, unknown>, bank: boolean) =>
    [
      p.name,
      p.taxId ? `ИИН/БИН/ИНН: ${p.taxId}` : '',
      p.address,
      bank && p.bank ? `Банк: ${p.bank}` : '',
      bank && p.account ? `Счёт: ${p.account}` : '',
      bank && p.bankCode ? `БИК/SWIFT: ${p.bankCode}` : '',
      bank && p.extra ? String(p.extra) : '',
      p.email,
    ]
      .filter(Boolean)
      .map(esc)
      .join('<br>');
  const period = i.period === 'year' ? 'год' : `${i.months} мес.`;
  const html = `<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>Счёт ${esc(i.number)}</title>
<style>@page{size:A4;margin:15mm}body{font:14px/1.45 Arial,sans-serif;color:#111;margin:0}h1{font-size:20px;margin:0 0 4px}
.cols{display:flex;gap:24px;margin:16px 0}.cols>div{flex:1}h3{font-size:12px;text-transform:uppercase;color:#555;margin:0 0 4px}
table{width:100%;border-collapse:collapse;margin-top:12px}td,th{border:1px solid #999;padding:6px 8px;text-align:left}td.n,th.n{text-align:right}
.sum{margin-top:12px;text-align:right}.words{margin-top:8px}.sign{margin-top:36px}</style></head><body>
<h1>Счёт на оплату № ${esc(i.number)}</h1><div>от ${esc(date(i.issuedOn))}, оплатить до ${esc(date(i.dueOn))}</div>
<div class="cols"><div><h3>Продавец</h3>${party(s, true)}</div><div><h3>Покупатель</h3>${party(b, false)}</div></div>
<table><tr><th>Наименование</th><th class="n">Период</th><th class="n">Сумма</th></tr>
<tr><td>Лицензия на портал, тариф «${esc(i.planName)}»</td><td class="n">${esc(period)}</td><td class="n">${esc(money(i.amountNet, i.currency))}</td></tr></table>
<div class="sum">Налог (${esc(i.vatRate)}%): ${esc(money(i.vatAmount, i.currency))}<br><b>Итого к оплате: ${esc(money(i.amountTotal, i.currency))}</b></div>
<div class="words">${esc(i.amountInWords)}</div>${s.signer ? `<div class="sign">Подпись: ______________ ${esc(s.signer)}</div>` : ''}
<script>window.onload=function(){window.print()}</script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) {
    toast('Браузер заблокировал окно печати — разрешите всплывающие окна для портала', 'danger');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function RequestDialog({
  open,
  onClose,
  license,
}: {
  open: boolean;
  onClose: () => void;
  license: CompanyLicense['license'];
}) {
  const qc = useQueryClient();
  const [period, setPeriod] = useState<'month' | 'year'>('year');
  const [months, setMonths] = useState(1);
  const [note, setNote] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<Invoice>('/admin/license/invoices', {
        method: 'POST',
        body: {
          planId: license?.planId,
          period,
          months: period === 'year' ? 12 : months,
          note: note.trim() || null,
        },
      }),
    onSuccess: (inv) => {
      toast(`Счёт ${inv.number} выставлен`);
      qc.invalidateQueries({ queryKey: INVOICES_KEY });
      onClose();
      setNote('');
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Запросить счёт"
      description={license ? `Оплата лицензии по тарифу «${license.planName}»` : undefined}
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!license}
            onClick={() => create.mutate()}
          >
            Выставить счёт
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Период оплаты" htmlFor="inv-period">
          <Select
            id="inv-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as 'month' | 'year')}
          >
            <option value="year">Год</option>
            <option value="month">Помесячно</option>
          </Select>
        </Field>
        {period === 'month' ? (
          <Field label="Сколько месяцев оплатить" htmlFor="inv-months">
            <Select id="inv-months" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, k) => k + 1).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Комментарий" htmlFor="inv-note" hint="Необязательно">
          <Input id="inv-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <p className="text-[13px] text-ink-2">
          Реквизиты покупателя берутся из блока «Реквизиты для счетов» ниже — заполните их до запроса.
        </p>
        {create.isError ? <ErrorBox error={create.error} /> : null}
      </div>
    </Dialog>
  );
}

function BuyerForm() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: BILLING_KEY, queryFn: () => api<BillingDetails>('/admin/billing') });
  const [f, setF] = useState<BillingDetails>(BillingDetails.parse({}));
  useEffect(() => {
    if (q.data) setF(q.data);
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api<BillingDetails>('/admin/billing', { method: 'PUT', body: f }),
    onSuccess: (d) => {
      toast('Реквизиты сохранены');
      qc.setQueryData(BILLING_KEY, d);
    },
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const errs = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const set = (k: keyof BillingDetails) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF({ ...f, [k]: e.target.value });
  return (
    <form
      className="grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Название организации" htmlFor="bd-name" error={errs.name} className="sm:col-span-2">
        <Input id="bd-name" value={f.name} maxLength={300} onChange={set('name')} />
      </Field>
      <Field label="БИН / ИНН" htmlFor="bd-tax" error={errs.taxId}>
        <Input id="bd-tax" value={f.taxId} maxLength={40} onChange={set('taxId')} />
      </Field>
      <Field label="Почта для счетов" htmlFor="bd-email" error={errs.email}>
        <Input id="bd-email" type="email" value={f.email} maxLength={200} onChange={set('email')} />
      </Field>
      <Field label="Адрес" htmlFor="bd-addr" error={errs.address} className="sm:col-span-2">
        <Input id="bd-addr" value={f.address} maxLength={500} onChange={set('address')} />
      </Field>
      {save.isError ? <ErrorBox error={save.error} className="sm:col-span-2" /> : null}
      <div className="sm:col-span-2">
        <Button type="submit" variant="primary" loading={save.isPending}>
          Сохранить реквизиты
        </Button>
      </div>
    </form>
  );
}

/** Счета на оплату лицензии (ЛЦ-06): список, запрос нового счёта, печатная форма, реквизиты покупателя */
export function BillingSection() {
  const lic = useQuery({
    queryKey: ['admin', 'license'],
    queryFn: () => api<CompanyLicense>('/admin/license'),
  });
  const q = useQuery({ queryKey: INVOICES_KEY, queryFn: () => api<Invoice[]>('/admin/license/invoices') });
  const [asking, setAsking] = useState(false);
  const license = lic.data?.license ?? null;
  return (
    <>
      <Panel
        title="Счета на оплату"
        meta="ЛЦ-06"
        actions={
          <Button variant="primary" size="sm" disabled={!license} onClick={() => setAsking(true)}>
            Запросить счёт
          </Button>
        }
      >
        {q.isPending ? <Spinner /> : null}
        {q.isError ? <ErrorBox error={q.error} /> : null}
        {q.data && !q.data.length ? <p className="text-[14px] text-ink-2">Счетов пока нет.</p> : null}
        {q.data?.length ? (
          <ul className="divide-y divide-line">
            {q.data.map((i) => {
              const st = statusOf(i);
              return (
                <li key={i.id} className="flex flex-col gap-1.5 py-3 text-[14px]">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <span className="font-medium text-ink">
                      № {i.number} · тариф «{i.planName}»
                    </span>
                    <Badge tone={st.tone}>{st.title}</Badge>
                  </div>
                  <div className="num text-[13px] text-ink-2">
                    {money(i.amountTotal, i.currency)} · выставлен {date(i.issuedOn)}, оплатить до{' '}
                    {date(i.dueOn)}
                    {i.paidOn ? `, оплачен ${date(i.paidOn)}` : ''}
                  </div>
                  {i.payments.length ? (
                    <div className="text-[13px] text-ink-2">
                      Платежи:{' '}
                      {i.payments
                        .map(
                          (p) =>
                            `${money(p.amount, i.currency)} (${PAYMENT_METHOD_TITLES[p.method]}, ${date(p.paidOn)})`,
                        )
                        .join('; ')}
                    </div>
                  ) : null}
                  <div>
                    <Button size="sm" onClick={() => printInvoice(i)}>
                      Печатная форма
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        <div className="mt-5 border-t border-line pt-4">
          <h3 className="mb-3 text-[14px] font-semibold text-ink">Реквизиты для счетов</h3>
          <BuyerForm />
        </div>
      </Panel>
      <RequestDialog open={asking} onClose={() => setAsking(false)} license={license} />
    </>
  );
}
